import React from 'react';
import styled from 'styled-components';

import { tokens } from 'containers/Intelligence/theme';

import { panelHeading } from '../panelHeading';

export interface IEvidenceChipRowProps {
  /** Rendered uppercase by the label type role; pass the copy in its natural case. */
  label: string;
  items: string[];
  className?: string;
}

const Label = styled.h4`
  ${panelHeading}
`;

const ChipList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  margin: ${tokens.space['8']} 0 calc(-1 * ${tokens.space['8']});
  padding: 0;
  list-style: none;
`;

/* Safari < 14.1 has no flex gap, and the `> * + *` fallback misplaces the first chip of every
   wrapped line — so each chip carries its own trailing margins and the list cancels the last
   row's (conventions/components/cross-browser.md § 5). */
const Chip = styled.li`
  margin: 0 ${tokens.space['8']} ${tokens.space['8']} 0;
  padding: ${tokens.space['4']} ${tokens.space['12']};
  border: 1px solid ${tokens.color['border-strong']};
  border-radius: ${tokens.radius.pill};
  color: ${tokens.color['text-secondary']};
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['label-line']};
`;

const EvidenceChipRow: React.FC<IEvidenceChipRowProps> = ({ label, items, className }) => {
  const events = Array.isArray(items) ? items.filter(item => Boolean(item)) : [];

  // The FR forbids fabricating evidence: a titled row with no chip would read as "no qualifying
  // events measured" rather than as absence, so the card shows its data state instead.
  if (events.length === 0) {
    return null;
  }

  return (
    <section className={className} data-testid="evidence-chip-row">
      <Label data-testid="evidence-chip-row-label">{label}</Label>
      <ChipList>
        {events.map(item => (
          <Chip key={item} data-testid="evidence-chip-row-item">
            {item}
          </Chip>
        ))}
      </ChipList>
    </section>
  );
};

export default EvidenceChipRow;
